import React, { useState, useEffect } from 'react';
import axios from 'axios';
import API_CONFIG from '../config/api';
import { getAuthToken, createAuthHeaders, withLoading, handleApiError } from '../utils/apiHelpers';
import { FaEye, FaTrash } from 'react-icons/fa';

const GalleryManager = () => {
  const [galleryData, setGalleryData] = useState([]);
  const [events, setEvents] = useState([]);
  const [formData, setFormData] = useState({
    eventName: '',
    eventDate: '',
    caption: '',
    isHighlight: false
  });
  const [photoFiles, setPhotoFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedEventPhotos, setSelectedEventPhotos] = useState([]);
  const [selectedEventName, setSelectedEventName] = useState('');
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // Helper function to get correct image URL (remove /api prefix for static files)
  const getImageUrl = (imagePath) => {
    if (!imagePath) return '/images/heroSection_img.jpg';
    // If imagePath already starts with /uploads/, use it directly
    if (imagePath.startsWith('/uploads/')) {
      return imagePath;
    }
    // Otherwise, assume it needs /uploads/ prefix
    return `/uploads/gallery/${imagePath}`;
  };

  useEffect(() => {
    fetchGalleryData();
    fetchEvents();
  }, []);

  // Keyboard navigation for slider
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (selectedEventPhotos.length === 0) return;
      
      if (e.key === 'ArrowLeft') {
        setCurrentImageIndex(prev => prev === 0 ? selectedEventPhotos.length - 1 : prev - 1);
      } else if (e.key === 'ArrowRight') {
        setCurrentImageIndex(prev => prev === selectedEventPhotos.length - 1 ? 0 : prev + 1);
      } else if (e.key === 'Escape') {
        setSelectedImage(null);
        setSelectedEventPhotos([]);
        setSelectedEventName('');
        setCurrentImageIndex(0);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [selectedEventPhotos.length]);

  // Debug: Log events when they change
  useEffect(() => {
    // console.log('📅 Events updated:', events.length, 'events');
    if (events.length > 0) {
      // console.log('📅 Sample event:', events[0]);
      // console.log('📅 All events:', events.map(e => ({ name: e.eventName, startDate: e.startDate })));
    } else {
      // console.log('❌ No events loaded');
    }
  }, [events]);

  // Debug: Log form data when it changes
  useEffect(() => {
    // console.log('📝 Form data updated:', formData);
  }, [formData]);

  const fetchGalleryData = async () => {
    try {
      await withLoading(setLoading, async () => {
        const response = await axios.get('/api/gallery/admin/grouped', {
          headers: createAuthHeaders()
        });
        setGalleryData(response.data);
      });
    } catch (error) {
      handleApiError(error, setError);
    }
  };

  const fetchEvents = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      // console.log('🔍 Fetching events for Gallery Manager...');
      const response = await axios.get('/api/events/all', {
        headers: { Authorization: `Bearer ${token}` }
      });
      // console.log('📅 Events fetched:', response.data.length, 'events');
      // console.log('📅 Sample event:', response.data[0]);
      setEvents(response.data);
    } catch (error) {
      // console.log('❌ Error fetching events:', error.message);
      // console.log('❌ Error response:', error.response?.data);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.eventName || photoFiles.length === 0) {
      alert('Please enter Event Name and select at least one photo');
      return;
    }

    try {
      setUploading(true);
      const token = localStorage.getItem('adminToken');
      let successCount = 0;
      let failCount = 0;

      // First, check if event exists or create a new one
      let eventId;
      try {
        // Try to find existing event by name
        const eventsResponse = await axios.get('/api/events/all', {
          headers: { Authorization: `Bearer ${token}` }
        });
        const existingEvent = eventsResponse.data.find(
          event => event.eventName.toLowerCase() === formData.eventName.toLowerCase()
        );

        if (existingEvent) {
          eventId = existingEvent._id;
        } else {
          // Create a new event if it doesn't exist
          const newEventData = new FormData();
          newEventData.append('eventName', formData.eventName);
          newEventData.append('startDate', formData.eventDate || new Date().toISOString());
          newEventData.append('endDate', formData.eventDate || new Date().toISOString());
          newEventData.append('description', `Gallery event for ${formData.eventName}`);
          newEventData.append('rules', 'Gallery Event');
          
          const eventResponse = await axios.post('/api/events', newEventData, {
            headers: { Authorization: `Bearer ${token}` }
          });
          eventId = eventResponse.data._id;
        }
      } catch (error) {
        // // console.log('Error handling event:', error);
        // If event creation fails, use a temporary ID
        eventId = 'temp-' + Date.now();
      }

      // Upload each photo
      for (const photoFile of photoFiles) {
        const photoFormData = new FormData();
        photoFormData.append('photo', photoFile);
        photoFormData.append('eventId', eventId);
        photoFormData.append('eventName', formData.eventName); // Add event name
        photoFormData.append('eventDate', formData.eventDate); // Add event date
        photoFormData.append('caption', formData.caption);

        photoFormData.append('isHighlight', formData.isHighlight);

        try {
          // console.log('Uploading photo:', photoFile.name);
          const response = await axios.post('/api/gallery/upload', photoFormData, {
            headers: { 
              Authorization: `Bearer ${token}`
              // Remove Content-Type header - let axios set it automatically for FormData
            }
          });
          // console.log('Upload response:', response.data);
          successCount++;
        } catch (error) {
          // // console.log('Error uploading photo:', error.response?.data || error.message);
          failCount++;
        }
      }

      if (successCount > 0) {
        alert(`${successCount} photos successfully uploaded! ${failCount > 0 ? `${failCount} photos failed.` : ''}`);
        
        // Reset form
        setFormData({
          eventName: '',
          eventDate: '',
          caption: '',
          isHighlight: false
        });
        setPhotoFiles([]);
        
        // Reset file input
        const fileInput = document.querySelector('input[type="file"]');
        if (fileInput) fileInput.value = '';
        
        // Refresh gallery
        fetchGalleryData();
      } else {
        alert('Error uploading photos. Please try again.');
      }
    } catch (error) {
      // // console.log('Error uploading photos:', error);
      alert('Error uploading photos. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleDeletePhoto = async (photoId) => {
    if (window.confirm('Are you sure you want to delete this photo?')) {
      try {
        const token = localStorage.getItem('adminToken');
        await axios.delete(`/api/gallery/${photoId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        alert('Photo deleted successfully!');
        fetchGalleryData();
      } catch (error) {
        // // console.log('Error deleting photo:', error);
        alert('Error deleting photo. Please try again.');
      }
    }
  };

  const handleDeleteEvent = async (eventName) => {
    if (window.confirm(`Are you sure you want to delete the entire event "${eventName}" and all its photos? This action cannot be undone.`)) {
      try {
        const token = localStorage.getItem('adminToken');
        const response = await axios.delete(`/api/gallery/event/${encodeURIComponent(eventName)}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        alert(`Event "${eventName}" deleted successfully! ${response.data.deletedPhotos} photos were removed.`);
        fetchGalleryData();
      } catch (error) {
        // // console.log('Error deleting event:', error);
        alert('Error deleting event. Please try again.');
      }
    }
  };

  const toggleHighlight = async (photoId, currentHighlight) => {
    try {
      const token = localStorage.getItem('adminToken');
              await axios.put(`/api/gallery/${photoId}`, {
        isHighlight: !currentHighlight
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchGalleryData();
    } catch (error) {
      // // console.log('Error updating highlight:', error);
      alert('Error updating highlight status.');
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">


      <form onSubmit={handleSubmit} className="bg-white p-6 rounded-lg shadow-md mb-6">
        <h3 className="text-xl font-semibold text-gray-800 mb-4">
          Upload New Photos
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Select Event *
            </label>
            <select
              value={formData.eventName}
              onChange={(e) => {
                const selectedEventName = e.target.value;
                // console.log('🎯 Event selected:', selectedEventName);
                // console.log('📅 Available events:', events.length);
                
                const selectedEvent = events.find(event => event.eventName === selectedEventName);
                // console.log('🔍 Selected event data:', selectedEvent);
                
                // Auto-populate event date if event is selected
                let eventDate = '';
                if (selectedEvent) {
                  // Use startDate (which contains the actual event date from API)
                  const eventDateValue = selectedEvent.startDate;
                  // console.log('📅 Event date value:', eventDateValue);
                  
                  if (eventDateValue) {
                    // Format date for input field (YYYY-MM-DD)
                    const date = new Date(eventDateValue);
                    eventDate = date.toISOString().split('T')[0];
                    // console.log('✅ Auto-filled date:', eventDate);
                  } else {
                    // console.log('❌ No date found in event data');
                  }
                } else {
                  // console.log('❌ Event not found in events array');
                }
                
                setFormData({ 
                  ...formData, 
                  eventName: selectedEventName,
                  eventDate: eventDate
                });
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            >
              <option value="">Select an event...</option>
              {events.map((event) => (
                <option key={event._id} value={event.eventName}>
                  {event.eventName}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Event Date (Auto-filled from selected event)
            </label>
            <input
              type="date"
              value={formData.eventDate || ''}
              onChange={(e) => setFormData({ ...formData, eventDate: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <p className="text-xs text-gray-500 mt-1">Date is automatically filled when you select an event</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Upload Photos *
            </label>
            <div className="relative">
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={(e) => setPhotoFiles(Array.from(e.target.files))}
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                required
              />
              <div className="flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md bg-white hover:bg-gray-50 transition-colors">
                <svg className="w-5 h-5 text-gray-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
                <span className="text-sm text-gray-600">
                  {photoFiles.length > 0 ? `${photoFiles.length} file(s) selected` : 'Choose files'}
                </span>
              </div>
            </div>
            {photoFiles.length > 0 && (
              <p className="text-sm text-green-600 mt-1 font-medium">
                ✓ {photoFiles.length} photos selected
              </p>
            )}
          </div>

        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Caption
          </label>
          <textarea
            value={formData.caption}
            onChange={(e) => setFormData({ ...formData, caption: e.target.value })}
            placeholder="Enter caption for photos..."
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
            rows="2"
          />
        </div>

        <div className="mb-4">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={formData.isHighlight}
              onChange={(e) => setFormData({ ...formData, isHighlight: e.target.checked })}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded mr-2"
            />
            <span className="text-sm text-gray-700">Show on Homepage (Highlight)</span>
          </label>
        </div>

        <button
          type="submit"
          className="w-full md:w-auto bg-blue-600 text-white px-6 py-3 rounded-md hover:bg-blue-700 disabled:opacity-50 transition-colors duration-200 font-medium text-sm flex items-center justify-center"
          disabled={uploading}
        >
          {uploading ? (
            <span className="flex items-center">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              Uploading...
            </span>
          ) : (
            <span className="flex items-center">
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
              Upload Photos
            </span>
          )}
        </button>
      </form>

      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-800">Gallery Events</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Preview
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider max-w-[200px]">
                  Event Name
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Photo Count
                </th>

                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="4" className="px-4 py-8 text-center text-gray-500">
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mr-2"></div>
                      Loading gallery events...
                    </div>
                  </td>
                </tr>
              ) : galleryData.length > 0 ? (
                galleryData.map((event) => (
                  <tr key={event.eventName} className="hover:bg-gray-50">
                    <td className="px-4 py-3 whitespace-nowrap">
                      {event.photos.length > 0 ? (
                        <div className="relative group">
                          <img
                            src={getImageUrl(event.photos[0].image)}
                            alt={event.photos[0].title || event.eventName}
                            className="w-16 h-16 object-cover rounded-lg cursor-pointer border border-gray-200 hover:border-blue-300 transition-colors"
                            onClick={() => {
                              // console.log('Opening modal for event:', event.eventName, 'with photos:', event.photos.length);
                              setSelectedEventPhotos(event.photos);
                              setSelectedEventName(event.eventName);
                              setSelectedImage(event.photos[0]);
                              setCurrentImageIndex(0);
                            }}
                            onError={(e) => {
                              // Prevent infinite loop by checking if we're already on fallback
                              if (!e.target.src.includes('/images/heroSection_img.jpg')) {
                                e.target.src = '/images/heroSection_img.jpg';
                              } else {
                                // If fallback also fails, hide the image and show placeholder
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'flex';
                              }
                            }}
                            onLoad={() => {
                              // console.log('Image loaded successfully:', event.photos[0].photoUrl);
                            }}
                          />

                          

                        </div>
                                              ) : null}
                    </td>
                    <td 
                      className="px-4 py-3 max-w-[200px] truncate cursor-help" 
                      title={event.eventName || 'N/A'}
                    >
                      <div className="text-sm font-medium text-gray-900 truncate">{event.eventName}</div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                      {event.photos.length} photos
                    </td>

                    <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-center">
                      <div className="flex items-center justify-center gap-2">
                        <div
                          onClick={() => {
                            setSelectedEventPhotos(event.photos);
                            setSelectedEventName(event.eventName);
                            setSelectedImage(event.photos[0]);
                            setCurrentImageIndex(0);
                          }}
                          className="bg-blue-100 hover:bg-blue-200 text-blue-600 w-8 h-8 rounded-md transition-colors duration-200 flex items-center justify-center cursor-pointer"
                          title="View photos"
                        >
                          <FaEye className="w-4 h-4" />
                        </div>
                        <div
                          onClick={() => handleDeleteEvent(event.eventName)}
                          className="bg-yellow-100 hover:bg-yellow-200 text-orange-600 w-8 h-8 rounded-md transition-colors duration-200 flex items-center justify-center cursor-pointer"
                          title="Delete event"
                        >
                          <FaTrash className="w-4 h-4" />
                        </div>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="4" className="px-4 py-8 text-center text-gray-500">
                    <div className="flex flex-col items-center">
                      <svg className="w-12 h-12 text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <p className="text-gray-500">No gallery events found</p>
                      <p className="text-sm text-gray-400">Upload some photos to get started</p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      
      {/* Photo Gallery Modal - Slider View */}
      {selectedEventPhotos.length > 0 && (
        <div className="fixed inset-0 bg-black/95 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="relative max-w-6xl w-full max-h-[90vh] overflow-hidden bg-white rounded-3xl shadow-2xl">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-t-3xl p-4 flex justify-between items-center border-b border-gray-200">
              <h3 className="text-xl font-bold text-gray-900">
                {selectedEventName || 'Event Photos'} - {currentImageIndex + 1} of {selectedEventPhotos.length}
              </h3>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => handleDeleteEvent(selectedEventName)}
                  className="inline-flex items-center gap-2 text-red-700 hover:text-red-900 font-medium bg-red-100 hover:bg-red-200 px-3 py-1.5 rounded-lg border border-red-300 transition-all duration-200 text-sm"
                  title="Delete entire event and all photos"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                  Delete Event
                </button>
                
                <button
                  onClick={() => {
                    setSelectedImage(null);
                    setSelectedEventPhotos([]);
                    setSelectedEventName('');
                    setCurrentImageIndex(0);
                  }}
                  className="text-gray-500 hover:text-gray-700 p-2 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            
            {/* Slider Container */}
            <div className="relative bg-black">
              {/* Main Image Display */}
              <div className="relative h-[70vh] flex items-center justify-center">
                {selectedEventPhotos[currentImageIndex] && (
                  <img
                    src={getImageUrl(selectedEventPhotos[currentImageIndex].image)}
                    alt={selectedEventPhotos[currentImageIndex].title || `Photo ${currentImageIndex + 1}`}
                    className="max-w-full max-h-full object-contain"
                    onError={(e) => {
                      e.target.src = '/images/heroSection_img.jpg';
                    }}
                  />
                )}
                
                {/* Navigation Arrows */}
                {selectedEventPhotos.length > 1 && (
                  <>
                    {/* Previous Button */}
                    <button
                      onClick={() => setCurrentImageIndex(prev => prev === 0 ? selectedEventPhotos.length - 1 : prev - 1)}
                      className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-white/20 hover:bg-white/30 text-white rounded-full w-12 h-12 flex items-center justify-center transition-all duration-200 backdrop-blur-sm"
                    >
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                      </svg>
                    </button>
                    
                    {/* Next Button */}
                    <button
                      onClick={() => setCurrentImageIndex(prev => prev === selectedEventPhotos.length - 1 ? 0 : prev + 1)}
                      className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-white/20 hover:bg-white/30 text-white rounded-full w-12 h-12 flex items-center justify-center transition-all duration-200 backdrop-blur-sm"
                    >
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  </>
                )}
                
                {/* Highlight Indicator */}
                {selectedEventPhotos[currentImageIndex]?.isHighlight && (
                  <div className="absolute top-4 right-4 bg-yellow-500 text-white px-3 py-1 rounded-full flex items-center gap-1 text-sm">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                    Featured
                  </div>
                )}
              </div>
              
              {/* Thumbnail Navigation */}
              {selectedEventPhotos.length > 1 && (
                <div className="bg-white p-4 border-t border-gray-200">
                  <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
                    {selectedEventPhotos.map((photo, index) => (
                      <button
                        key={photo.id || photo._id}
                        onClick={() => setCurrentImageIndex(index)}
                        className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition-all duration-200 ${
                          index === currentImageIndex 
                            ? 'border-blue-500 ring-2 ring-blue-200' 
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <img
                          src={getImageUrl(photo.image)}
                          alt={photo.title || `Photo ${index + 1}`}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.target.src = '/images/heroSection_img.jpg';
                          }}
                        />
                      </button>
                    ))}
                  </div>
                  <div className="text-center text-xs text-gray-500 mt-2">
                    Click thumbnails to navigate • Use arrow keys or swipe
                  </div>
                </div>
              )}
              
              {/* Photo Info */}
              <div className="bg-white p-4 border-t border-gray-200">
                <div className="flex justify-between items-center">
                  <div>
                    <h4 className="font-semibold text-gray-900">
                      {selectedEventPhotos[currentImageIndex]?.title || `Photo ${currentImageIndex + 1}`}
                    </h4>
                    {selectedEventPhotos[currentImageIndex]?.uploadDate && (
                      <p className="text-sm text-gray-500">
                        Uploaded: {new Date(selectedEventPhotos[currentImageIndex].uploadDate).toLocaleDateString('en-IN')}
                      </p>
                    )}
                  </div>
                  
                  {/* Delete Current Photo Button */}
                  <button
                    onClick={() => handleDeletePhoto(selectedEventPhotos[currentImageIndex].id || selectedEventPhotos[currentImageIndex]._id)}
                    className="text-red-600 hover:text-red-800 bg-red-50 hover:bg-red-100 px-3 py-1.5 rounded-lg text-sm transition-colors duration-200 flex items-center gap-1"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                    Delete Photo
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GalleryManager; 
